import angular from 'angular';
import moment from 'moment';
import { buildMovimentacaoPeriodoTableItem } from '../../helpers/movimentacoes-periodos-ui.helpers';
import {
    IMovimentacaoPeriodoForm,
    IMovimentacaoPeriodoTableItem
} from '../../models/movimentacoes-periodos-ui.model';
import {
    BuildCreateFormCallback,
    CreatePeriodoCallback,
    GetPeriodoCallback,
    UpdatePeriodoCallback
} from './movimentacoes-periodos.modal.model';
import {
    IMovimentacaoPeriodo,
    IMovimentacaoPeriodoUpdatePayload
} from '../../../models/movimentacoes-periodos.service.model';
import { extractMovimentacaoErrorMessage } from '../../helpers/movimentacoes-errors.helpers';

type NewToasterType = {
    pop: (opts: { type: string; title: string; body?: string }) => void;
};

export class MovimentacoesPeriodosModalController {
    static $inject = [
        '$uibModalInstance',
        'periodos',
        'createPeriodo',
        'updatePeriodo',
        'buildCreateForm',
        'getPeriodo',
        'canManageMovimentacoes',
        'canEditPeriodo',
        'NewToaster'
    ];

    public novoPeriodoOpen = false;
    public creating = false;
    public editBusy = false;
    public editingRowId: string | null = null;
    public createModel: IMovimentacaoPeriodoForm;
    public editModel: IMovimentacaoPeriodoForm = this.createEmptyEditModel();
    public tableItems: IMovimentacaoPeriodoTableItem[] = [];
    public canManageMovimentacoes = false;
    public canEditPeriodo = false;

    constructor(
        private $uibModalInstance: ng.ui.bootstrap.IModalInstanceService,
        public periodos: IMovimentacaoPeriodo[],
        private createPeriodoCallback: CreatePeriodoCallback,
        private updatePeriodoCallback: UpdatePeriodoCallback,
        private buildCreateFormCallback: BuildCreateFormCallback,
        private getPeriodoCallback: GetPeriodoCallback,
        canManageMovimentacoes: boolean,
        canEditPeriodo: boolean,
        private NewToaster: NewToasterType
    ) {
        this.canManageMovimentacoes = !!canManageMovimentacoes;
        this.canEditPeriodo = !!canEditPeriodo;
        this.createModel = angular.copy(this.buildCreateFormCallback());
        this.refreshTableItems();
    }

    public close(): void {
        this.$uibModalInstance.dismiss('cancelar');
    }

    public toggleNovoPeriodo(): void {
        if (!this.canManageMovimentacoes) {
            return;
        }

        this.novoPeriodoOpen = !this.novoPeriodoOpen;
        this.createModel = angular.copy(this.buildCreateFormCallback());
        if (this.novoPeriodoOpen) {
            this.cancelEdit();
        }
    }

    public cancelCreate(): void {
        this.novoPeriodoOpen = false;
        this.createModel = angular.copy(this.buildCreateFormCallback());
    }

    public submitCreate(payload: IMovimentacaoPeriodoForm): void {
        if (!this.canManageMovimentacoes || this.creating) {
            return;
        }

        this.creating = true;
        const result = this.createPeriodoCallback(angular.copy(payload));

        if (result && typeof (result as angular.IPromise<unknown>).then === 'function') {
            (result as angular.IPromise<IMovimentacaoPeriodo | void>).then((createdPeriodo) => {
                if (createdPeriodo) {
                    this.upsertPeriodo(createdPeriodo);
                }
                this.cancelCreate();
                this.refreshTableItems();
            }).catch(() => undefined).finally(() => {
                this.creating = false;
            });
            return;
        }

        if (result) {
            this.upsertPeriodo(result as IMovimentacaoPeriodo);
        }
        this.cancelCreate();
        this.refreshTableItems();
        this.creating = false;
    }

    public startEdit(item: IMovimentacaoPeriodoTableItem): void {
        if (!this.canManageMovimentacoes || !item || !item.periodo || this.editBusy || !this.getPeriodoCallback) {
            return;
        }

        this.cancelCreate();
        this.cancelEdit();
        this.editBusy = true;

        const result = this.getPeriodoCallback(item.id);
        const applyPeriodo = (periodo: IMovimentacaoPeriodo): void => {
            this.upsertPeriodo(periodo);
            this.refreshTableItems();
            this.editingRowId = item.id;
            this.editModel = {
                mes: periodo.mes,
                ano: periodo.ano,
                encerramentolancamentos: periodo.encerramentolancamentos,
                situacao: Number(periodo.situacao)
            };
        };

        if (result && typeof (result as angular.IPromise<unknown>).then === 'function') {
            (result as angular.IPromise<IMovimentacaoPeriodo>)
                .then((periodo) => {
                    applyPeriodo(periodo);
                })
                .catch((error) => {
                    this.handleLoadPeriodoError(error);
                })
                .finally(() => {
                    this.editBusy = false;
                });
            return;
        }

        try {
            applyPeriodo(result as IMovimentacaoPeriodo);
        } catch (error) {
            this.handleLoadPeriodoError(error);
        } finally {
            this.editBusy = false;
        }
    }

    public cancelEdit(): void {
        this.editingRowId = null;
        this.editModel = this.createEmptyEditModel();
    }

    public isEditing(itemId: string): boolean {
        return this.editingRowId === itemId;
    }

    public submitEdit(item: IMovimentacaoPeriodoTableItem, payload: IMovimentacaoPeriodoForm): void {
        if (!this.canManageMovimentacoes || !this.canEditPeriodo || this.editBusy || !item || !item.periodo) {
            return;
        }

        const normalizedDate = this.normalizeDateInput(payload.encerramentolancamentos);
        const situacao = Number(payload.situacao);
        const versao = typeof item.versao === 'number' ? item.versao : 1;

        if (!normalizedDate || [1, 2].indexOf(situacao) === -1) {
            return;
        }

        this.editBusy = true;
        const updatePayload: IMovimentacaoPeriodoUpdatePayload = {
            situacao,
            encerramentolancamentos: normalizedDate,
            versao
        };

        const result = this.updatePeriodoCallback({
            periodo: angular.copy(item.periodo),
            payload: updatePayload
        });

        if (result && typeof (result as angular.IPromise<unknown>).then === 'function') {
            (result as angular.IPromise<IMovimentacaoPeriodo | void>).then((updatedPeriodo) => {
                if (updatedPeriodo) {
                    this.upsertPeriodo(updatedPeriodo);
                } else {
                    this.applyPeriodoLocalUpdate(item.id, updatePayload);
                }
                this.cancelEdit();
                this.refreshTableItems();
            }).catch(() => undefined).finally(() => {
                this.editBusy = false;
            });
            return;
        }

        if (result) {
            this.upsertPeriodo(result as IMovimentacaoPeriodo);
        } else {
            this.applyPeriodoLocalUpdate(item.id, updatePayload);
        }
        this.cancelEdit();
        this.refreshTableItems();
        this.editBusy = false;
    }

    private refreshTableItems(): void {
        const sorted = (this.periodos || [])
            .slice()
            .sort((a, b) => {
                if (a.ano !== b.ano) {
                    return b.ano - a.ano;
                }

                return b.mes - a.mes;
            });

        this.tableItems = sorted.map((periodo) =>
            buildMovimentacaoPeriodoTableItem(periodo)
        );
    }

    private upsertPeriodo(periodo: IMovimentacaoPeriodo): void {
        const index = (this.periodos || []).findIndex(
            (current) => current.movimentacaoperiodo === periodo.movimentacaoperiodo
        );

        if (index >= 0) {
            this.periodos[index] = angular.copy(periodo);
            return;
        }

        this.periodos.push(angular.copy(periodo));
    }

    private applyPeriodoLocalUpdate(identifier: string, payload: IMovimentacaoPeriodoUpdatePayload): void {
        const index = (this.periodos || []).findIndex((periodo) => periodo.movimentacaoperiodo === identifier);
        if (index < 0) {
            return;
        }

        const current = this.periodos[index];
        this.periodos[index] = {
            ...current,
            situacao: payload.situacao,
            encerramentolancamentos: payload.encerramentolancamentos,
            versao: (current.versao || payload.versao || 1) + 1
        };
    }

    private normalizeDateInput(value: string | Date): string | null {
        if (value instanceof Date) {
            const parsedDate = moment(value);
            return parsedDate.isValid() ? parsedDate.format('YYYY-MM-DD') : null;
        }

        if (typeof value === 'string') {
            const parsedDate = moment(value.trim(), 'YYYY-MM-DD', true);
            return parsedDate.isValid() ? parsedDate.format('YYYY-MM-DD') : null;
        }

        return null;
    }

    private createEmptyEditModel(): IMovimentacaoPeriodoForm {
        return {
            mes: 0,
            ano: 0,
            encerramentolancamentos: '',
            situacao: 1
        };
    }

    private handleLoadPeriodoError(error: any): void {
        const message = extractMovimentacaoErrorMessage(error) || 'Não foi possível carregar os dados atualizados do período.';

        this.NewToaster.pop({
            type: 'error',
            title: 'Não foi possível carregar o período.',
            body: message
        });
    }
}
